// Compact only the policy ledger needed to re-evaluate Step02 strategy.
// Product facts and snapshots are server-owned by the batch job; forwarding
// them inside every policy row caused avoidable oversized request bodies.

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function text(value, limit = 240) {
  return String(value ?? '').trim().slice(0, limit);
}

function textList(value, { limit = 12, itemLimit = 120 } = {}) {
  const seen = new Set();
  return asArray(value)
    .map((item) => text(item, itemLimit))
    .filter((item) => item && !seen.has(item) && seen.add(item))
    .slice(0, limit);
}

function compactEvidence(evidence = {}) {
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) return {};
  const compact = {
    sources: textList(evidence.sources, { limit: 8, itemLimit: 48 }),
    sourceAsins: textList(evidence.sourceAsins, { limit: 8, itemLimit: 24 }),
    externalBrand: text(evidence.externalBrand, 120),
  };
  return Object.fromEntries(Object.entries(compact).filter(([, value]) => (
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  )));
}

/** Pure, bounded request DTO used by both strategy refresh and pre-check. */
export function compactKeywordUsePoliciesForStep2(policies = [], limit = 160) {
  const seen = new Set();
  const boundedLimit = Math.min(200, Math.max(1, Number(limit) || 160));
  const compact = [];
  for (const entry of asArray(policies)) {
    const keyword = text(entry?.keyword || entry?.normalizedKeyword || entry?.term || '', 160);
    const key = keyword.toLowerCase();
    const policy = text(entry?.policy, 40);
    if (!keyword || !policy || seen.has(key)) continue;
    seen.add(key);
    compact.push({
      schemaVersion: text(entry?.schemaVersion, 48) || 'keyword-use-policy.v1',
      keyword,
      normalizedKeyword: key,
      policy,
      preferredFields: textList(entry?.preferredFields, { limit: 6, itemLimit: 32 }),
      blockedFields: textList(entry?.blockedFields, { limit: 6, itemLimit: 32 }),
      reason: text(entry?.reason, 360),
      risks: textList(entry?.risks, { limit: 8, itemLimit: 120 }),
      semanticRole: text(entry?.semanticRole, 48),
      source: text(entry?.source, 80),
      sourceCategory: text(entry?.sourceCategory, 48),
      score: Number.isFinite(Number(entry?.score)) ? Number(entry.score) : 0,
      manuallyAdjusted: entry?.manuallyAdjusted === true,
      evidence: compactEvidence(entry?.evidence),
    });
    if (compact.length >= boundedLimit) break;
  }
  return compact;
}
